<template>
  <v-container class="mxw-1000 pt-8">
    <v-row>
      <v-col>
        <h1>{{ $bct('Welcome') }}, {{ $store.state.profile.first_name }}</h1>
      </v-col>
    </v-row>

    <v-row v-if="accessRequestsEnabled">
      <v-col>
        <v-card
          border
          tile
        >
          <v-card-title>
            <h1 class="fs-24">
              {{ $bct('Request access') }}
            </h1>
          </v-card-title>

          <v-divider />

          <v-card-text>
            <p class="mb-4">
              <!-- eslint-disable-next-line max-len vue/max-attributes-per-line -->
              {{
                $bct(
                  "You've successfully created an account, but you currently don't have permission to view any locations. To access a specific location, please use our search feature to find it and then request access.",
                )
              }}
            </p>

            <template v-if="accessRequest">
              <v-alert
                class="ta-left"
                color="primary"
                data-cy="access-request-status"
                type="info"
              >
                <v-row>
                  <v-col>
                    <p class="mb-4">
                      {{
                        $bct(
                          'Your request to join the following location has been submitted and will be confirmed shortly. Stay tuned!',
                        )
                      }}
                    </p>

                    <p
                      v-if="accessRequest.provider_name"
                      class="my-3 fw-800 fs-24 underlined"
                    >
                      {{ $bct(accessRequest.provider_name) }}
                    </p>
                  </v-col>
                </v-row>

                <v-row
                  class="ta-right"
                  dense
                >
                  <v-col>
                    <v-btn
                      @click="destroyAccessRequest"
                      data-cy="access-request-destroy-button"
                      variant="outlined"
                    >
                      {{ $bct('Cancel request') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-alert>
            </template>

            <template v-else>
              <v-row class="mb-4">
                <v-col>
                  <v-autocomplete
                    v-model="providerId"
                    @update:search="searchProviders"
                    @vue:mounted="searchProviders('')"
                    :aria-label="$bct('Select from one of the following providers')"
                    :items="providers"
                    data-cy="provider-search"
                    item-title="name"
                    item-value="id"
                    variant="filled"
                    auto-select-first
                    bottom
                    hide-details
                  >
                    <template #item="{ item, props }">
                      <v-list-item
                        v-bind="props"
                        :title="getProviderDisplayText(item.raw)"
                      />
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>

              <v-btn
                @click="createAccessRequest"
                :disabled="!providerId"
                :ripple="false"
                class="mb-4"
                color="primary"
                data-cy="access-request-button"
                size="x-large"
              >
                {{ $bct('Request to join location') }}
              </v-btn>

              <v-row>
                <v-col>
                  <p>
                    {{ $bct(CONTACT_SUPPORT_MESSAGE) }}
                  </p>

                  <p class="mx-auto">
                    <a
                      v-text="$store.state.site.provider_support_email"
                      :href="'mailto:' + $store.state.site.provider_support_email"
                    />
                  </p>
                </v-col>
              </v-row>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="onboardingFormSchemaId">
      <v-col>
        <v-card
          border
          tile
        >
          <v-card-title class="d-flex align-center">
            <h1 class="fs-24">
              {{ $bct(getThankYouMessage()) }}
            </h1>

            <template v-if="onboardingFormSubmitted">
              <v-spacer />

              <v-icon
                color="success"
                icon="check_circle"
                size="28"
              />
            </template>
          </v-card-title>

          <v-divider />

          <v-card-text>
            <template v-if="onboardingFormSubmitted">
              <p>
                {{ $bct(ONBOARDING_FORM_SUBMITTED_MESSAGE) }}
                <a
                  :href="`mailto:${EMAILS.SUPPORT}`"
                  :v-text="EMAILS.SUPPORT"
                />
              </p>
            </template>

            <template v-else>
              <p>
                {{ $bct(ACCESS_LOCATION_MESSAGE) }}
              </p>
            </template>
          </v-card-text>

          <template v-if="!onboardingFormSubmitted">
            <v-divider />

            <v-card-actions>
              <v-btn
                :to="{
                  name: 'OnboardingForm',
                  params: {
                    schemaId: onboardingFormSchemaId,
                  },
                }"
                append-icon="east"
                color="primary"
                size="x-large"
              >
                {{ $bct('Start application') }}
              </v-btn>
            </v-card-actions>
          </template>
        </v-card>

        <template v-if="!(accessRequestsEnabled || onboardingFormSchemaId)">
          <p class="mb-8">
            {{ $bct(CREATE_SUCCESS_MESSAGE) }}
          </p>
        </template>
      </v-col>
    </v-row>
  </v-container>

  <ConfirmDialog ref="confirmDialog" />
</template>

<script setup>
import { useStore } from 'vuex';

import Api from '@/manager/services/bright_finder';
import ConfirmDialog from '@/shared/components/ConfirmDialog.vue';
import { EMAILS } from '@/shared/assets/constants';
import useEventBus from '@/shared/composables/useEventBus';

const $bct = inject('translator');

const store = useStore();

const ACCESS_LOCATION_MESSAGE =
  'To get access to the appropriate locations, please fill out the ' +
  'onboarding application. Once you do so, a member of the administrator team will be in touch.';
const CONTACT_SUPPORT_MESSAGE =
  "If you can't locate the specific place you're looking for," +
  " don't hesitate to contact our support team for assistance at:";
const CREATE_SUCCESS_MESSAGE =
  'You have successfully created an account, but you currently do not have permission ' +
  'to view any locations. To access a specific location, please contact your administrator.';
const ONBOARDING_FORM_SUBMITTED_MESSAGE =
  'Once you are approved as a designee, you will receive an email. ' +
  'If you have any questions in the meantime, please email:';

const accessRequest = ref(null);
const confirmDialog = ref(null);
const eventBus = useEventBus();
const onboardingForm = ref(null);
const onboardingFormSchemaId = ref(null);
const providerId = ref(null);
const providers = ref([]);
const processing = ref(false);
const queryCount = ref(0);

const accessRequestsEnabled = computed(() => {
  return store.state.config.enable_provider_access_request;
});

const onboardingFormSubmitted = computed(() => {
  return !!onboardingForm.value?.attributes?.submitted_at;
});

async function createAccessRequest() {
  if (!accessRequestsEnabled.value) return;
  if (accessRequest.value) {
    eventBus.error('You may not create more than one request for a location/business');
    return;
  }

  processing.value = true;
  const provider = providers.value.find((p) => p.id === providerId.value);
  const business_id = provider?.business_id;

  const response = await Api.manager.accessRequest.create({
    provider_id: provider.id,
    business_id: business_id,
  });
  processing.value = false;

  accessRequest.value = response?.data;
}

async function destroyAccessRequest() {
  const confirmed = await confirmDialog.value.confirmWithText(
    'Are you sure you want to cancel this access request?',
    {
      title: 'Access Request',
      confirmButtonText: 'Cancel Request',
      cancelButtonText: 'Keep Request',
      saveButtonColor: 'red',
    },
  );
  if (confirmed) {
    processing.value = true;
    await Api.manager.accessRequest.destroy(accessRequest.value.id);
    providerId.value = null;
    processing.value = false;
    accessRequest.value = null;
  }
}

// NOTE: This all operates under the pre-existing understanding of a single access request per user
//       and so it grabs the first request (while assuming that there is only one -- which is VERY funky)
//       and if there is one it doesn't allow them to request access any more (allowing multiple requests
//       would/could create a situation where they have simultaneous requests to multiple businesses that
//       if approved would create a bad state.... this should probably all be locked down both
//       frontend and backend
async function getAccessRequest() {
  const response = await Api.manager.accessRequest.index();
  accessRequest.value = response?.data?.length > 0 ? response.data[0] : null;
}

function getProviderDisplayText(provider) {
  if (!provider.location) return provider.name;
  return `${provider.name} - ${provider.location}`;
}

function getThankYouMessage() {
  if (onboardingFormSubmitted.value) return 'Thanks for submitting your application!';
  return 'Thanks for setting up your account!';
}

async function load() {
  await loadOnboardingForm();
  if (accessRequestsEnabled.value) await getAccessRequest();
}

async function loadOnboardingForm() {
  onboardingFormSchemaId.value = store.state.config.provider_onboarding_form_schema_id;

  if (!onboardingFormSchemaId.value) return false;

  const response = await Api.manager.form.index({
    'filter[schema]': onboardingFormSchemaId.value,
  });
  const forms = response.data.data || [];
  onboardingForm.value = forms[forms.length - 1];

  return true;
}

function searchProviders(query) {
  queryCount.value += 1;
  const currentQueryCount = queryCount.value;
  Api.publicApi.organization.provider.index({ query, page_size: 200 }, (response) => {
    if (queryCount.value === currentQueryCount) {
      providers.value = response.data.filter((provider) => provider.business_id !== null);
    }
  });
}

onMounted(load);
</script>
